import { useState, useEffect } from "react";
import Swipeable from "@/components/Swipeable";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft,
  Check,
  X,
  Shield,
  Clock,
  Heart,
  Building2,
  Ambulance,
  Stethoscope,
  Percent,
  AlertCircle,
  Award,
  FileDown,
  Share2,
  User,
  Calendar,
  ChevronDown,
  ChevronUp,
  Loader2,
  CheckCircle2,
  Sparkles,
  ExternalLink
} from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// IRDAI Claim Experience Data
const CLAIM_EXPERIENCE_DATA = {
  "hdfc-ergo": {
    claim_settlement_ratio: "~96%",
    incurred_claim_ratio: "84.85%",
    icr_previous_year: "80.98%",
    icr_trend: "Increased",
    network_hospitals: "13,000+",
    cashless_hospitals: "10,000+",
    irdai_health_category: "Private General Insurer"
  },
  "care-health": {
    claim_settlement_ratio: "~92%",
    incurred_claim_ratio: "64.53%",
    icr_previous_year: "57.69%",
    icr_trend: "Increased",
    network_hospitals: "24,800+",
    cashless_hospitals: "21,000+",
    irdai_health_category: "Standalone Health Insurer"
  },
  "icici-lombard": {
    claim_settlement_ratio: "~94%",
    incurred_claim_ratio: "82.24%",
    icr_previous_year: "78.85%",
    icr_trend: "Increased",
    network_hospitals: "10,800+",
    cashless_hospitals: "9,000+",
    irdai_health_category: "Private General Insurer"
  },
  "aditya-birla": {
    claim_settlement_ratio: "~93%",
    incurred_claim_ratio: "71.50%",
    icr_previous_year: "68.31%",
    icr_trend: "Increased",
    network_hospitals: "10,500+",
    cashless_hospitals: "10,000+",
    irdai_health_category: "Standalone Health Insurer"
  },
  "manipal-cigna": {
    claim_settlement_ratio: "~93.7%",
    incurred_claim_ratio: "64%",
    icr_previous_year: "65%",
    icr_trend: "Stable",
    network_hospitals: "8,000+",
    cashless_hospitals: "7,500+",
    irdai_health_category: "Standalone Health Insurer"
  }
};

// Comparison Categories
const COMPARISON_CATEGORIES = [
  {
    id: "claim_experience",
    name: "Claim Experience (IRDAI FY 2024-25)",
    icon: Award,
    color: "blue",
    fields: [
      { key: "claim_settlement_ratio", label: "Claim Settlement Ratio (CSR)" },
      { key: "incurred_claim_ratio", label: "Incurred Claim Ratio (ICR)" },
      { key: "icr_previous_year", label: "ICR Previous Year" },
      { key: "icr_trend", label: "ICR Trend" },
      { key: "network_hospitals", label: "Network Hospitals" },
      { key: "cashless_hospitals", label: "Cashless Hospitals" },
    ]
  },
  {
    id: "basic",
    name: "Basic Policy Details",
    icon: Shield,
    color: "slate",
    fields: [
      { key: "sum_insured_display", label: "Sum Insured Options" },
      { key: "room_rent", label: "Room Rent" },
      { key: "icu_limit", label: "ICU Limit" },
      { key: "policy_tenure", label: "Policy Tenure" },
      { key: "family_floater", label: "Family Floater" },
    ]
  },
  {
    id: "hospitalization",
    name: "Hospitalization Coverage",
    icon: Building2,
    color: "emerald",
    fields: [
      { key: "pre_hospitalization", label: "Pre-Hospitalization" },
      { key: "post_hospitalization", label: "Post-Hospitalization" },
      { key: "day_care", label: "Day Care Procedures" },
      { key: "domiciliary", label: "Domiciliary Treatment" },
      { key: "organ_donor", label: "Organ Donor Coverage" },
    ]
  },
  {
    id: "ambulance",
    name: "Ambulance & Emergency",
    icon: Ambulance,
    color: "red",
    fields: [
      { key: "road_ambulance", label: "Road Ambulance" },
      { key: "air_ambulance", label: "Air Ambulance" },
    ]
  },
  {
    id: "restore",
    name: "Restore & Bonus Benefits",
    icon: Percent,
    color: "purple",
    fields: [
      { key: "restore_benefit", label: "Restore/Recharge Benefit" },
      { key: "ncb", label: "No Claim Bonus (NCB)" },
      { key: "ncb_reduction", label: "NCB Reduction on Claim" },
    ]
  },
  {
    id: "ayush",
    name: "AYUSH & Alternative",
    icon: Heart,
    color: "green",
    fields: [
      { key: "ayush_coverage", label: "AYUSH Coverage" },
      { key: "ayush_pre_post", label: "AYUSH Pre/Post Hospitalization" },
    ]
  },
  {
    id: "waiting",
    name: "Waiting Periods",
    icon: Clock,
    color: "orange",
    fields: [
      { key: "initial_waiting", label: "Initial Waiting Period" },
      { key: "specific_disease_waiting", label: "Specific Disease Waiting" },
      { key: "pre_existing_waiting", label: "Pre-Existing Disease (PED)" },
      { key: "ped_modification", label: "PED Modification Option" },
    ]
  },
  {
    id: "additional",
    name: "Additional Benefits",
    icon: Stethoscope,
    color: "teal",
    fields: [
      { key: "health_checkup", label: "Annual Health Checkup" },
      { key: "consumables", label: "Consumables Coverage" },
      { key: "robotics", label: "Robotic Surgery" },
      { key: "second_opinion", label: "Second E-Opinion" },
    ]
  },
  {
    id: "optional",
    name: "Optional Coverages (Riders)",
    icon: Sparkles,
    color: "amber",
    fields: [
      { key: "maternity", label: "Maternity Coverage" },
      { key: "opd", label: "OPD Coverage" },
      { key: "hospital_cash", label: "Hospital Daily Cash" },
      { key: "critical_illness", label: "Critical Illness" },
    ]
  },
  {
    id: "copay",
    name: "Co-payments & Limits",
    icon: AlertCircle,
    color: "yellow",
    fields: [
      { key: "copay_age", label: "Co-payment (Age Based)" },
      { key: "copay_zone", label: "Zonal Co-payment" },
      { key: "sub_limits", label: "Sub-Limits" },
    ]
  },
  {
    id: "discounts",
    name: "Discounts Available",
    icon: Percent,
    color: "gold",
    fields: [
      { key: "multi_year_discount", label: "Multi-Year Discount" },
      { key: "family_discount", label: "Family Discount" },
      { key: "online_discount", label: "Online Purchase Discount" },
    ]
  }
];

const getCompanyId = (plan) => {
  const company = plan?.company?.toLowerCase() || "";
  if (company.includes('hdfc')) return 'hdfc-ergo';
  if (company.includes('care')) return 'care-health';
  if (company.includes('icici')) return 'icici-lombard';
  if (company.includes('aditya') || company.includes('birla')) return 'aditya-birla';
  if (company.includes('manipal') || company.includes('cigna')) return 'manipal-cigna';
  return null;
};

// Extract comparison data from plan
const extractComparisonData = (plan) => {
  if (!plan) return {};
  
  const getRoomRent = () => {
    const baseRoomRent = plan.coverage_details?.hospitalization?.room_rent || "Covered up to SI";
    if (plan.id === 'icici-elevate') return `${baseRoomRent} (Any Room available)`;
    return baseRoomRent;
  };
  
  const getAirAmbulance = () => {
    const baseAir = plan.coverage_details?.ambulance?.air || "Not Covered";
    const airRider = plan.riders?.find(r => r.name.toLowerCase().includes('air ambulance'));
    if (airRider) return `${baseAir} (via Rider)`;
    return baseAir;
  };
  
  const getRoadAmbulance = () => plan.coverage_details?.ambulance?.road || "Covered";
  
  const getMaternity = () => {
    const maternityRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('maternity') || r.name.toLowerCase().includes('parenthood')
    );
    if (maternityRider) {
      const waitingPeriod = maternityRider.waiting_period || "24 months";
      return `Optional: ${maternityRider.name} (WP: ${waitingPeriod})`;
    }
    return "Not Covered";
  };
  
  const getOPD = () => {
    const opdRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('opd') || r.name.toLowerCase().includes('wellbeing') || r.name.toLowerCase().includes('befit')
    );
    if (opdRider) return `Optional: ${opdRider.name}`;
    return "Not Covered";
  };
  
  const getConsumables = () => {
    const consumableRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('consumable') || r.name.toLowerCase().includes('claim shield') || r.name.toLowerCase().includes('claim protector')
    );
    if (consumableRider) return `Covered via ${consumableRider.name}`;
    return "Covered up to SI";
  };
  
  const getRestoreBenefit = () => {
    const restoreBenefit = plan.core_benefits?.find(b => 
      b.name.toLowerCase().includes('restore') || b.name.toLowerCase().includes('recharge') || 
      b.name.toLowerCase().includes('reset') || b.name.toLowerCase().includes('secure')
    );
    if (restoreBenefit) {
      const detail = restoreBenefit.details?.[0] || restoreBenefit.description;
      return detail || "Available";
    }
    return "Available";
  };
  
  const getNCB = () => {
    const ncbBenefit = plan.core_benefits?.find(b => 
      b.name.toLowerCase().includes('bonus') || b.name.toLowerCase().includes('cumulative') || b.name.toLowerCase().includes('loyalty')
    );
    if (ncbBenefit) {
      const detail = ncbBenefit.details?.[0] || ncbBenefit.description;
      return detail || "50% per year up to 100%";
    }
    return "50% per year up to 100%";
  };
  
  const getNCBReduction = () => {
    const ncbBenefit = plan.core_benefits?.find(b => 
      b.name.toLowerCase().includes('bonus') || b.name.toLowerCase().includes('cumulative')
    );
    if (ncbBenefit?.details) {
      const reduction = ncbBenefit.details.find(d => 
        d.toLowerCase().includes('reduce') || d.toLowerCase().includes('claim') || d.toLowerCase().includes('maintain')
      );
      return reduction || "May reduce on claim";
    }
    return "May reduce on claim";
  };
  
  const getPEDModification = () => {
    const pedInfo = plan.waiting_periods?.pre_existing?.reduction;
    const pedRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('ped') || r.name.toLowerCase().includes('jumpstart') || r.name.toLowerCase().includes('pre-existing')
    );
    if (pedRider) return `${pedInfo || 'Standard'} (${pedRider.name} available)`;
    return pedInfo || "Standard (36 months)";
  };
  
  const getHealthCheckup = () => {
    if (plan.health_checkup) {
      const limits = plan.health_checkup.limits_individual || plan.health_checkup.limits_floater;
      if (limits) {
        const firstKey = Object.keys(limits)[0];
        return `${limits[firstKey]}`;
      }
      if (plan.health_checkup.availability) return plan.health_checkup.availability;
    }
    return "Covered as per plan";
  };
  
  const getHospitalCash = () => {
    const hcRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('hospital cash') || r.name.toLowerCase().includes('daily cash')
    );
    if (hcRider) return `Optional: ${hcRider.name}`;
    return "Not Available";
  };
  
  const getCriticalIllness = () => {
    const ciRider = plan.riders?.find(r => r.name.toLowerCase().includes('critical illness'));
    if (ciRider) {
      const limit = ciRider.details?.find(d => d.includes('₹') || d.includes('Lakh'));
      return `Optional: ${ciRider.name}${limit ? ` (${limit})` : ''}`;
    }
    return "Not Covered";
  };
  
  const getAYUSHPrePost = () => {
    const ayushInfo = plan.coverage_details?.ayush;
    if (ayushInfo?.pre_post) return ayushInfo.pre_post;
    return "Inpatient Only";
  };
  
  const getMultiYearDiscount = () => {
    const discount = plan.discounts?.find(d => 
      d.name.toLowerCase().includes('multi') || d.name.toLowerCase().includes('long') || d.name.toLowerCase().includes('tenure')
    );
    if (discount) return discount.description || discount.discount || "7.5% (2yr) / 10% (3yr)";
    return "7.5% (2yr) / 10% (3yr)";
  };
  
  const data = {
    sum_insured_display: plan.sum_insured_options?.slice(0, 4).join(", ") + (plan.sum_insured_options?.length > 4 ? "..." : "") || "N/A",
    room_rent: getRoomRent(),
    icu_limit: plan.coverage_details?.hospitalization?.icu_charges || "Covered up to SI",
    policy_tenure: plan.policy_options?.tenure?.join(", ") || "1, 2, 3 Years",
    family_floater: plan.policy_options?.max_members_floater || "Available",
    pre_hospitalization: plan.coverage_details?.pre_hospitalization?.days || "60 Days",
    post_hospitalization: plan.coverage_details?.post_hospitalization?.days || "180 Days",
    day_care: plan.coverage_details?.day_care?.procedures || "All Covered",
    domiciliary: plan.coverage_details?.domiciliary?.coverage || "Covered up to SI",
    organ_donor: plan.coverage_details?.organ_donor?.coverage || "Covered up to SI",
    road_ambulance: getRoadAmbulance(),
    air_ambulance: getAirAmbulance(),
    restore_benefit: getRestoreBenefit(),
    ncb: getNCB(),
    ncb_reduction: getNCBReduction(),
    ayush_coverage: plan.coverage_details?.ayush?.coverage || "Covered up to SI",
    ayush_pre_post: getAYUSHPrePost(),
    initial_waiting: plan.waiting_periods?.initial?.period || "30 Days",
    specific_disease_waiting: plan.waiting_periods?.specific_diseases?.period || "24 Months",
    pre_existing_waiting: plan.waiting_periods?.pre_existing?.period || "36 Months",
    ped_modification: getPEDModification(),
    health_checkup: getHealthCheckup(),
    consumables: getConsumables(),
    robotics: plan.coverage_details?.advanced_technology?.coverage || plan.coverage_details?.modern_treatments?.coverage || "Covered up to SI",
    second_opinion: "Available",
    maternity: getMaternity(),
    opd: getOPD(),
    hospital_cash: getHospitalCash(),
    critical_illness: getCriticalIllness(),
    copay_age: "No Co-pay",
    copay_zone: "No Zonal Co-pay (Pan India)",
    sub_limits: getRoomRent().includes("No sub-limit") || getRoomRent().includes("No Capping") || getRoomRent().includes("At Actuals") ? "No Sub-limits" : "May have sub-limits",
    multi_year_discount: getMultiYearDiscount(),
    family_discount: plan.discounts?.find(d => d.name.toLowerCase().includes('family'))?.discount || "Available",
    online_discount: plan.discounts?.find(d => d.name.toLowerCase().includes('online'))?.discount || "Available",
  };
  
  const companyId = getCompanyId(plan);
  if (companyId && CLAIM_EXPERIENCE_DATA[companyId]) {
    const claimData = CLAIM_EXPERIENCE_DATA[companyId];
    data.claim_settlement_ratio = claimData.claim_settlement_ratio;
    data.incurred_claim_ratio = claimData.incurred_claim_ratio;
    data.icr_previous_year = claimData.icr_previous_year;
    data.icr_trend = claimData.icr_trend;
    data.network_hospitals = claimData.network_hospitals;
    data.cashless_hospitals = claimData.cashless_hospitals;
  }
  
  return data;
};

export default function SharedQuotation() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(COMPARISON_CATEGORIES.map(c => c.id));
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const fetchQuotation = async () => {
      try {
        const response = await fetch(`${API_URL}/api/quotations/${quoteId}`);
        if (!response.ok) {
          throw new Error("Quotation not found");
        }
        const data = await response.json();
        setQuotation(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchQuotation();
  }, [quoteId]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(window.location.href);
    alert("Link copied to clipboard!");
  };

  const loadImageAsBase64 = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  const generatePDF = async () => {
    if (!quotation || !quotation.plans) return;
    
    setGenerating(true);
    
    try {
      const planDetails = quotation.plans;
      const logoPromises = planDetails.map(p => loadImageAsBase64(p.company_logo));
      const logos = await Promise.all(logoPromises);

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;
      
      const navy = [15, 23, 42];
      const gold = [217, 119, 6];
      const white = [255, 255, 255];
      const lightGray = [248, 250, 252];
      const borderColor = [226, 232, 240];

      // Header
      doc.setFillColor(...navy);
      doc.rect(0, 0, pageWidth, 28, 'F');
      doc.setFillColor(...gold);
      doc.rect(0, 28, pageWidth, 2, 'F');
      
      doc.setTextColor(...white);
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("HEALTH INSURANCE COMPARISON", margin, 12);
      
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text(`Prepared for: ${quotation.client_name}${quotation.client_age ? ` (Age: ${quotation.client_age})` : ''}`, margin, 20);
      doc.text(`Date: ${formatDate(quotation.quote_date)}`, margin, 25);
      
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...gold);
      doc.text("Insurance Insights by Prashant K Uunadkat", pageWidth - margin, 12, { align: 'right' });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...white);
      doc.text("Beshak Verified Advisor | +91 9510020907", pageWidth - margin, 20, { align: 'right' });

      // Plan Cards with Logos
      let yPos = 35;
      const cardWidth = (pageWidth - (2 * margin) - ((planDetails.length - 1) * 5)) / planDetails.length;
      const cardHeight = 28;
      
      planDetails.forEach((plan, index) => {
        const xPos = margin + (index * (cardWidth + 5));
        
        doc.setFillColor(240, 245, 250);
        doc.roundedRect(xPos, yPos, cardWidth, cardHeight, 3, 3, 'F');
        doc.setDrawColor(...navy);
        doc.setLineWidth(0.3);
        doc.roundedRect(xPos, yPos, cardWidth, cardHeight, 3, 3, 'S');
        
        if (logos[index]) {
          try {
            doc.addImage(logos[index], 'PNG', xPos + 4, yPos + 4, 20, 20);
          } catch (e) {
            doc.setFillColor(...navy);
            doc.rect(xPos + 4, yPos + 4, 20, 20, 'F');
          }
        }
        
        doc.setTextColor(...navy);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        const planName = plan.name.replace("ManipalCigna ", "").replace("HDFC ERGO ", "").replace("Care ", "").replace("ICICI Lombard ", "").replace("Aditya Birla ", "");
        doc.text(planName, xPos + 28, yPos + 12, { maxWidth: cardWidth - 32 });
        
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 100, 100);
        doc.text(plan.company, xPos + 28, yPos + 19, { maxWidth: cardWidth - 32 });
      });

      yPos += cardHeight + 5;

      const colCount = planDetails.length + 1;
      const labelColWidth = 55;
      const planColWidth = (pageWidth - (2 * margin) - labelColWidth) / planDetails.length;
      const comparisonData = planDetails.map(plan => extractComparisonData(plan));
      
      const body = [];
      const categoryColors = {
        claim_experience: [59, 130, 246],
        basic: [15, 23, 42],
        hospitalization: [34, 197, 94],
        ambulance: [239, 68, 68],
        restore: [139, 92, 246],
        ayush: [34, 139, 34],
        waiting: [220, 53, 69],
        additional: [0, 128, 128],
        optional: [139, 69, 19],
        copay: [255, 140, 0],
        discounts: [217, 119, 6]
      };
      
      COMPARISON_CATEGORIES.forEach(category => {
        body.push([{ 
          content: category.name, 
          colSpan: colCount, 
          styles: { 
            fillColor: categoryColors[category.id] || navy, 
            textColor: white, 
            fontStyle: 'bold', 
            halign: 'center',
            fontSize: 9,
            cellPadding: 3
          } 
        }]);
        
        category.fields.forEach(field => {
          const row = [field.label];
          comparisonData.forEach(data => {
            row.push(data[field.key] || "N/A");
          });
          body.push(row);
        });
      });

      autoTable(doc, {
        body: body,
        startY: yPos,
        margin: { left: margin, right: margin },
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          overflow: 'linebreak',
          lineColor: borderColor,
          lineWidth: 0.2,
          valign: 'middle'
        },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: labelColWidth, fillColor: lightGray, halign: 'left', fontSize: 8 },
          ...Object.fromEntries(
            planDetails.map((_, i) => [i + 1, { cellWidth: planColWidth, halign: 'center', fontSize: 8 }])
          )
        },
        alternateRowStyles: { fillColor: [255, 255, 255] },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index > 0) {
            const text = String(data.cell.raw || '');
            if (text.toLowerCase().includes('covered') || text.toLowerCase().includes('available') || 
                text.toLowerCase().includes('unlimited') || text.toLowerCase().includes('no co-pay')) {
              data.cell.styles.textColor = [34, 139, 34];
            }
            if (text.toLowerCase().includes('not covered') || text.toLowerCase().includes('not available')) {
              data.cell.styles.textColor = [200, 50, 50];
            }
          }
        }
      });

      // Footer on all pages
      const totalPages = doc.internal.getNumberOfPages();
      
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        
        doc.setFillColor(...lightGray);
        doc.rect(0, pageHeight - 12, pageWidth, 12, 'F');
        doc.setDrawColor(...gold);
        doc.setLineWidth(0.5);
        doc.line(0, pageHeight - 12, pageWidth, pageHeight - 12);
        
        doc.setFontSize(6);
        doc.setTextColor(100, 100, 100);
        doc.setFont("helvetica", "normal");
        doc.text("Disclaimer: Premium is indicative and subject to medical underwriting. GST @18% applicable. This is not a policy document.", margin, pageHeight - 7);
        
        doc.setFontSize(7);
        doc.setTextColor(50, 50, 50);
        doc.text("Contact: +91 9510020907 | prasant@shreefinservices.com", margin, pageHeight - 3);
        
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...gold);
        doc.text(`Insurance Insights by Prashant K Uunadkat  |  Page ${i}/${totalPages}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
      }

      const fileName = `Quote_${quotation.client_name.replace(/\s+/g, '_')}_${formatDate(quotation.quote_date).replace(/\s+/g, '-').replace(/,/g, '')}.pdf`;
      doc.save(fileName);
      
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const renderValue = (value, field) => {
    if (value === null || value === undefined || value === "N/A" || value === "Not Available" || value === "Not Covered") {
      return (
        <span className="text-red-600 flex items-center justify-center gap-1">
          <X className="h-4 w-4" />
          <span className="text-sm">{value || "Not Available"}</span>
        </span>
      );
    }
    
    if (field.key.includes('_url')) {
      if (value) {
        return (
          <a 
            href={value} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[#A87608] hover:text-[#A87608] underline text-sm flex items-center justify-center gap-1"
          >
            View <ExternalLink className="h-3 w-3" />
          </a>
        );
      }
      return <span className="text-[#3A4557] text-sm">Not Available</span>;
    }
    
    if (
      value.toLowerCase().includes('covered') || 
      value.toLowerCase().includes('unlimited') ||
      value.toLowerCase().includes('no co-pay') ||
      value.toLowerCase().includes('no sub-limit') ||
      value.toLowerCase().includes('available')
    ) {
      return (
        <span className="text-emerald-600 flex items-center justify-center gap-1">
          <Check className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm">{value}</span>
        </span>
      );
    }
    
    return <span className="text-[#2E3B57] text-sm">{value}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-[#A87608] animate-spin mx-auto mb-4" />
          <p className="text-[#3A4557] text-lg">Loading quotation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#1B2A4A] mb-2">Quotation Not Found</h2>
          <p className="text-[#3A4557] mb-6">This quotation link may have expired or is invalid.</p>
          <Button onClick={() => navigate("/")} className="bg-[#D4A017] hover:bg-[#D4A017] text-[#0D1A3A]">
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  const plans = quotation?.plans || [];
  const comparisonData = plans.map(plan => ({
    plan,
    data: extractComparisonData(plan)
  }));

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-[#C9D4E6] bg-[#EDF1F8]/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/")}
                className="text-[#3A4557] hover:text-[#1B2A4A]"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Home
              </Button>
              
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-[#1B2A4A]">Health Insurance Comparison</h1>
                <p className="text-[#3A4557] text-sm">Prepared for {quotation?.client_name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={copyShareLink}
                className="border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A]"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Copy Link
              </Button>
              <Button
                onClick={generatePDF}
                disabled={generating}
                className="bg-gradient-to-r from-[#D4A017] via-yellow-500 to-[#D9A82C] text-[#0D1A3A] font-bold rounded-full shadow-[0_0_15px_rgba(251,191,36,0.2)]"
              >
                {generating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                ) : (
                  <><FileDown className="h-4 w-4 mr-2" />Download PDF</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Client Info Bar */}
      <div className="bg-white/50 border-b border-[#C9D4E6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-wrap items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-[#A87608]" />
              <span className="text-[#3A4557]">Client:</span>
              <span className="text-[#1B2A4A] font-medium">{quotation?.client_name}</span>
            </div>
            {quotation?.client_age && (
              <div className="flex items-center gap-2">
                <span className="text-[#3A4557]">Age:</span>
                <span className="text-[#1B2A4A] font-medium">{quotation?.client_age} years</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#A87608]" />
              <span className="text-[#3A4557]">Date:</span>
              <span className="text-[#1B2A4A] font-medium">{formatDate(quotation?.quote_date)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Plan Cards */}
        <div className="grid gap-4 mb-8" style={{ gridTemplateColumns: `repeat(${plans.length}, minmax(200px, 1fr))` }}>
          {plans.map((plan) => (
            <div key={plan.id} className="bg-white/50 backdrop-blur-md rounded-2xl border border-[#C9D4E6] p-5 text-center">
              <div className="h-16 w-16 rounded-xl bg-white p-2 mx-auto mb-4">
                <img 
                  src={plan.company_logo}
                  alt={plan.company}
                  className="h-full w-full object-contain"
                />
              </div>
              <h3 className="font-bold text-[#1B2A4A] text-lg mb-1">{plan.name}</h3>
              <p className="text-[#3A4557] text-sm">{plan.company}</p>
            </div>
          ))}
        </div>

        {/* Comparison Categories */}
        {COMPARISON_CATEGORIES.map((category) => {
          const Icon = category.icon;
          const isExpanded = expandedCategories.includes(category.id);
          
          const colorClasses = {
            blue: "bg-[#EDF1F8]/40 text-[#2E3B57]",
            slate: "bg-[#E2E9F4]/50 text-[#2E3B57]",
            emerald: "bg-emerald-500/20 text-emerald-600",
            red: "bg-red-500/20 text-red-600",
            purple: "bg-[#EDF1F8]/40 text-[#2E3B57]",
            green: "bg-green-500/20 text-green-600",
            orange: "bg-orange-500/20 text-orange-600",
            teal: "bg-teal-500/20 text-teal-600",
            amber: "bg-[#D4A017]/15 text-[#A87608]",
            yellow: "bg-yellow-500/20 text-yellow-600",
            gold: "bg-[#D4A017]/15 text-[#A87608]"
          };
          
          return (
            <div key={category.id} className="bg-white/50 backdrop-blur-md rounded-2xl border border-[#C9D4E6] mb-4 overflow-hidden">
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full flex items-center justify-between p-5 hover:bg-[#EDF1F8]/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${colorClasses[category.color] || colorClasses.slate}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h2 className="font-bold text-[#1B2A4A] text-base sm:text-lg">{category.name}</h2>
                </div>
                {isExpanded ? (
                  <ChevronUp className="h-5 w-5 text-[#3A4557]" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-[#3A4557]" />
                )}
              </button>
              
              {isExpanded && (
                <div className="border-t border-[#C9D4E6]"><Swipeable hint>
                  <div className="min-w-[600px]">
                    {category.fields.map((field, fieldIndex) => (
                      <div 
                        key={field.key}
                        className={`grid gap-4 p-4 ${fieldIndex % 2 === 0 ? 'bg-[#EDF1F8]/20' : ''}`}
                        style={{ gridTemplateColumns: `220px repeat(${plans.length}, minmax(180px, 1fr))` }}
                      >
                        <div className="flex items-center">
                          <span className="text-[#3A4557] text-sm font-medium">{field.label}</span>
                        </div>
                        
                        {comparisonData.map(({ plan, data }) => (
                          <div key={plan.id} className="flex items-center justify-center text-center px-2">
                            {renderValue(data[field.key], field)}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </Swipeable></div>
              )}
            </div>
          );
        })}

        {/* Selected Riders Section */}
        {quotation?.selected_riders && Object.values(quotation.selected_riders).some(r => r.length > 0) && (
          <div className="bg-white/50 backdrop-blur-md rounded-2xl border border-[#C9D4E6] mb-4 overflow-hidden">
            <div className="p-5 border-b border-[#C9D4E6]">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-[#EDF1F8]/40 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-[#2E3B57]" />
                </div>
                <h2 className="font-bold text-[#1B2A4A] text-lg">Selected Riders for This Quote</h2>
              </div>
            </div>
            <div className="p-5"><Swipeable hint>
              <div className="min-w-[600px]">
                {(() => {
                  const allRiderNames = new Set();
                  plans.forEach(p => {
                    const planRiderIds = quotation.selected_riders[p.id] || [];
                    planRiderIds.forEach(riderId => {
                      const rider = p.riders?.find(r => r.id === riderId);
                      if (rider) allRiderNames.add(rider.name);
                    });
                  });
                  
                  return Array.from(allRiderNames).map((riderName, idx) => (
                    <div 
                      key={riderName}
                      className={`grid gap-4 p-4 ${idx % 2 === 0 ? 'bg-[#EDF1F8]/20' : ''} rounded-lg`}
                      style={{ gridTemplateColumns: `220px repeat(${plans.length}, minmax(180px, 1fr))` }}
                    >
                      <div className="flex items-center">
                        <span className="text-[#1B2A4A] font-medium text-sm">{riderName}</span>
                      </div>
                      
                      {plans.map(plan => {
                        const planRiderIds = quotation.selected_riders[plan.id] || [];
                        const rider = plan.riders?.find(r => r.name === riderName && planRiderIds.includes(r.id));
                        if (rider) {
                          return (
                            <div key={plan.id} className="text-center">
                              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-600 text-sm">
                                <CheckCircle2 className="h-4 w-4" />
                                Selected
                              </span>
                              <p className="text-[#3A4557] text-xs mt-2">{rider.description}</p>
                            </div>
                          );
                        }
                        return (
                          <div key={plan.id} className="text-center text-[#3A4557] text-sm">-</div>
                        );
                      })}
                    </div>
                  ));
                })()}
              </div>
            </Swipeable></div>
          </div>
        )}

        {/* Premium Section */}
        {quotation?.plan_premiums && Object.values(quotation.plan_premiums).some(p => Object.values(p).some(v => v)) && (
          <div className="bg-white/50 backdrop-blur-md rounded-2xl border border-[#C9D4E6] mb-4 overflow-hidden">
            <div className="p-5 border-b border-[#C9D4E6]">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-[#D4A017]/15 flex items-center justify-center">
                  <span className="text-[#A87608] font-bold text-lg">₹</span>
                </div>
                <h2 className="font-bold text-[#1B2A4A] text-lg">Premium Details (Indicative + 18% GST)</h2>
              </div>
            </div>
            <div className="p-5"><Swipeable hint>
              <div className="min-w-[600px]">
                {["25L", "50L", "1Cr"].map((tier, idx) => {
                  const hasAnyPremium = plans.some(p => quotation.plan_premiums[p.id]?.[tier]);
                  if (!hasAnyPremium) return null;
                  
                  return (
                    <div 
                      key={tier}
                      className={`grid gap-4 p-4 ${idx % 2 === 0 ? 'bg-[#EDF1F8]/20' : ''} rounded-lg`}
                      style={{ gridTemplateColumns: `220px repeat(${plans.length}, minmax(180px, 1fr))` }}
                    >
                      <div className="flex items-center">
                        <span className="text-[#1B2A4A] font-medium text-sm">₹{tier} Sum Insured</span>
                      </div>
                      
                      {plans.map(plan => {
                        const premium = quotation.plan_premiums[plan.id]?.[tier];
                        return (
                          <div key={plan.id} className="text-center">
                            {premium ? (
                              <span className="text-[#A87608] font-bold text-lg">
                                ₹{Number(premium).toLocaleString('en-IN')}/-
                              </span>
                            ) : (
                              <span className="text-[#3A4557]">-</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </Swipeable></div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="bg-white/50 backdrop-blur-md rounded-2xl border border-[#C9D4E6] p-5 mt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-[#A87608] flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-[#1B2A4A] mb-2">Important Disclaimer</h4>
              <p className="text-[#3A4557] text-sm">
                This comparison is for informational purposes only. Premium is indicative and subject to medical underwriting. 
                GST @18% is applicable. Please refer to the official policy documents for complete terms and conditions. 
                Always consult with your insurance advisor before making a decision.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 border-t border-[#C9D4E6] mt-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
              <span className="font-bold text-xl text-[#0D1A3A]">P</span>
            </div>
            <div>
              <p className="font-bold text-[#1B2A4A]">Prashant K Uunadkat</p>
              <p className="text-xs text-[#3A4557]">Beshak Verified Advisor | Shree Finserv</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4 text-[#3A4557] text-sm">
            <a href="tel:+919510020907" className="hover:text-[#A87608] transition-colors">+91 9510020907</a>
            <span>•</span>
            <a href="mailto:prasant@shreefinservices.com" className="hover:text-[#A87608] transition-colors">prasant@shreefinservices.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
